/**
 * Pure sizing math: how big does each structure need to be, and what accuracy
 * does a given size buy? These functions are the analytical counterparts to the
 * implementations and drive the lab's calculators. Every formula is the
 * standard one from the literature; the tests pin them to known values.
 */

const LN2 = Math.LN2;
const LN2_SQUARED = LN2 * LN2;

// ----------------------------------------------------------------------------
// Bloom filter
// ----------------------------------------------------------------------------

/** Optimal bit count m for n items at false-positive rate p. */
export function bloomBits(n: number, p: number): number {
  return Math.ceil((-n * Math.log(p)) / LN2_SQUARED);
}

/** Optimal number of hash functions k given m bits and n items. */
export function bloomHashes(m: number, n: number): number {
  return Math.max(1, Math.round((m / n) * LN2));
}

/** False-positive rate of an m-bit, k-hash filter holding n items. */
export function bloomFalsePositiveRate(m: number, n: number, k: number): number {
  return Math.pow(1 - Math.exp((-k * n) / m), k);
}

/** Bytes of memory for an m-bit filter. */
export function bloomMemoryBytes(m: number): number {
  return Math.ceil(m / 8);
}

// ----------------------------------------------------------------------------
// Cuckoo filter
// ----------------------------------------------------------------------------

/** Fingerprint bits needed for target FPR with a given bucket size. */
export function cuckooFingerprintBits(p: number, bucketSize = 4): number {
  return Math.ceil(Math.log2((2 * bucketSize) / p));
}

/** FPR of a cuckoo filter ≈ 2·bucketSize / 2^fingerprintBits. */
export function cuckooFalsePositiveRate(fingerprintBits: number, bucketSize = 4): number {
  return (2 * bucketSize) / 2 ** fingerprintBits;
}

/** Bytes for a cuckoo filter holding n items at ~95% load. */
export function cuckooMemoryBytes(
  n: number,
  fingerprintBits: number,
  bucketSize = 4,
  loadTarget = 0.95,
): number {
  const buckets = nextPow2(Math.ceil(n / bucketSize / loadTarget));
  const bits = buckets * bucketSize * fingerprintBits;
  return Math.ceil(bits / 8);
}

// ----------------------------------------------------------------------------
// HyperLogLog
// ----------------------------------------------------------------------------

/** Relative standard error at precision p: 1.04 / sqrt(2^p). */
export function hllStandardError(precision: number): number {
  return 1.04 / Math.sqrt(2 ** precision);
}

/** Smallest precision in [4, 18] whose standard error is ≤ target. */
export function hllPrecisionForError(targetError: number): number {
  const p = Math.ceil(Math.log2((1.04 / targetError) ** 2));
  return Math.min(18, Math.max(4, p));
}

/**
 * Bytes for a dense HLL at precision p. Real implementations pack each register
 * into 6 bits (a leading-zero run over a 64-bit hash never exceeds ~64).
 */
export function hllMemoryBytes(precision: number, bitsPerRegister = 6): number {
  return Math.ceil((2 ** precision * bitsPerRegister) / 8);
}

// ----------------------------------------------------------------------------
// Count-Min Sketch
// ----------------------------------------------------------------------------

/** Columns for additive error factor ε: ceil(e/ε). */
export function cmsWidth(epsilon: number): number {
  return Math.ceil(Math.E / epsilon);
}

/** Rows for failure probability δ: ceil(ln(1/δ)). */
export function cmsDepth(delta: number): number {
  return Math.ceil(Math.log(1 / delta));
}

/** Additive error ε·N for width w over a stream of total count N. */
export function cmsError(width: number, totalCount: number): number {
  return (Math.E / width) * totalCount;
}

/** Bytes for a w×d sketch with 4-byte counters. */
export function cmsMemoryBytes(width: number, depth: number, bytesPerCounter = 4): number {
  return width * depth * bytesPerCounter;
}

// ----------------------------------------------------------------------------
// MinHash
// ----------------------------------------------------------------------------

/** Standard error of a Jaccard estimate at similarity j with k hashes. */
export function minhashError(j: number, k: number): number {
  return Math.sqrt((j * (1 - j)) / k);
}

/**
 * Hash count for a worst-case standard error target. Variance j(1−j) peaks at
 * j = 0.5, so k = ceil(0.25 / target^2) bounds the error everywhere.
 */
export function minhashHashesForError(targetError: number): number {
  return Math.ceil(0.25 / targetError ** 2);
}

/** Bytes for a k-slot signature with 8-byte (64-bit) values. */
export function minhashMemoryBytes(k: number, bytesPerSlot = 8): number {
  return k * bytesPerSlot;
}

// ----------------------------------------------------------------------------
// DDSketch (quantiles)
// ----------------------------------------------------------------------------

/** The geometric base γ = (1+α)/(1−α) for relative accuracy α. */
export function ddSketchGamma(alpha: number): number {
  return (1 + alpha) / (1 - alpha);
}

/** Buckets needed to cover [minValue, maxValue] at relative accuracy α. */
export function ddSketchBuckets(minValue: number, maxValue: number, alpha: number): number {
  if (minValue <= 0 || maxValue <= minValue) return 1;
  return Math.ceil(Math.log(maxValue / minValue) / Math.log(ddSketchGamma(alpha)));
}

/** Bytes for a DDSketch (≈ one index+count entry per bucket). */
export function ddSketchMemoryBytes(buckets: number, bytesPerBucket = 12): number {
  return buckets * bytesPerBucket;
}

// ----------------------------------------------------------------------------
// Shared helpers
// ----------------------------------------------------------------------------

/** Smallest power of two ≥ x (and ≥ 1). */
export function nextPow2(x: number): number {
  if (x <= 1) return 1;
  return 1 << Math.ceil(Math.log2(x));
}

/** Bytes an *exact* set/map of n keys would use, as a rough baseline. Assumes
 *  a hash-table entry of ~48 bytes per key (pointer, hash, overhead). */
export function exactSetMemoryBytes(n: number, bytesPerEntry = 48): number {
  return n * bytesPerEntry;
}
